import { init } from '@media-platform/push-notifications/src/client';

import { IInstance, IState, TReadyInstance, IAppDefaults } from '../types';

import { MODEL_PARAMS_DEFAULT } from '../constants/settings';

import {
  checkTimeMark,
  clearTimemark,
  getPermission,
  observePermission,
  setUnsubscribeFlag,
  updateTimeMark,
} from '../utils';

type EventName = 'ready' | 'change' | 'error' | 'subscribe' | 'unsubscribe';
type EventsCallback = (data: unknown) => void;
type Events = Partial<Record<EventName, EventsCallback>>;

type Params = {
  isEnabled: boolean;
  useLocalStorageTimemark: boolean;
  timeMarkDelta: number;
};

type Permission = {
  permission: {
    browserPermission: boolean;
    userPermission: boolean;
  };
};

type IStateWithPermission = IState & Permission;

export { Permission, IStateWithPermission, Events, Params };

export class PushNotificationsModel {
  private options: Partial<IAppDefaults> | null = null;
  private instance: IInstance | null = null;
  private on: Events | null = null;
  private params: Params;

  constructor(options: Partial<IAppDefaults>, params: Partial<Params>) {
    this.options = options;
    this.instance = init(this.options);
    this.params = this.setParams(params);
  }

  /**
   * Инициализация прослушки событий
   */
  public get init() {
    this.addListener('ready', this.handleReady.bind(this));
    this.addListener('change', this.handleChange.bind(this));
    this.addListener('subscribe', this.handleSubscribe.bind(this));
    this.addListener('unsubscribe', this.handleUnsubscribe.bind(this));
    this.addListener('error', this.handleError.bind(this));

    return this;
  }

  /**
   * Установка событий
   */
  public events(on: Events | null) {
    this.on = on;
  }

  /**
   * Готовность инстанса `push-notifications`
   */
  public ready(): Promise<TReadyInstance> | undefined {
    return this.instance?.ready();
  }

  /**
   * Переключение подписки
   */
  public toggle() {
    return clearTimemark(this.params.useLocalStorageTimemark)
      .then(() => this.ready())
      .then((inst) => inst?.toggle())
      .then((inst) => {
        const state = inst?.getState();
        setUnsubscribeFlag(!state?.isSubscribed);
        return state?.isSubscribed ? inst : this.defer().then(() => inst);
      });
  }

  /**
   * Подписка
   */
  public subscribe() {
    const { timeMarkDelta, useLocalStorageTimemark } = this.params;
    return checkTimeMark(timeMarkDelta, useLocalStorageTimemark, true)
      .then(() => {
        return clearTimemark(useLocalStorageTimemark)
          .then(() => this.ready())
          .then((inst) => {
            const state = inst?.getState();
            setUnsubscribeFlag(false);
            return !state?.isSubscribed ? inst?.subscribe() : inst;
          });
      })
      .catch(this.handleError.bind(this));
  }

  /**
   * Отписка
   */
  public unsubscribe() {
    return this.defer()
      .then(() => this.ready())
      .then((inst) => {
        const state = inst?.getState();
        setUnsubscribeFlag(true);
        return state?.isSubscribed ? inst?.unsubscribe() : inst;
      });
  }

  /**
   * Очистка временной метки
   */
  public clear() {
    return clearTimemark(this.params.useLocalStorageTimemark).then(() =>
      this.ready()
    );
  }

  /**
   * Отложить на время появление уведомления
   */
  public defer() {
    const { timeMarkDelta, useLocalStorageTimemark } = this.params;
    return updateTimeMark(timeMarkDelta, useLocalStorageTimemark).catch(
      this.handleError.bind(this)
    );
  }

  /**
   * Удаление всех обработчиков
   */
  public destroyAllListener() {
    this.removeAddListener('ready', this.handleReady.bind(this));
    this.removeAddListener('change', this.handleChange.bind(this));
    this.removeAddListener('subscribe', this.handleSubscribe.bind(this));
    this.removeAddListener('unsubscribe', this.handleUnsubscribe.bind(this));
    this.removeAddListener('error', this.handleError.bind(this));
  }

  /**
   * Удаление инстанса
   */
  public destroy() {
    this.destroyAllListener();
    this.on = null;
    this.options = null;
    this.instance = null;
  }

  /**
   * Установка парметров
   */
  private setParams = (params: Partial<Params>): Params => {
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        if (params[key as keyof Params] === undefined) {
          // Удаляем поля с undefined, чтобы использовать "MODEL_PARAMS_DEFAULT".
          delete params[key as keyof Params];
        }
      }
    }

    return {
      ...MODEL_PARAMS_DEFAULT,
      ...params,
    };
  };

  private addListener(eventName: EventName, cb: (...args: unknown[]) => void) {
    if (!cb) return;
    this.instance?.on(eventName, cb);
  }

  private removeAddListener(
    eventName: EventName,
    cb: (...args: unknown[]) => void
  ) {
    if (!cb) return;
    this.instance?.off(eventName, cb);
  }

  private handleChange(data: unknown) {
    if (typeof this.on?.change === 'function') {
      this.on?.change(data);
    }
  }

  private handleSubscribe(data: unknown) {
    if (typeof this.on?.subscribe === 'function') {
      this.on?.subscribe(data);
    }
  }

  private handleUnsubscribe(data: unknown) {
    if (typeof this.on?.unsubscribe === 'function') {
      this.on?.unsubscribe(data);
    }
  }

  private handleError(error: unknown) {
    if (typeof this?.on?.error === 'function') {
      this?.on?.error(error);
    }
    window.console.warn('[PushNotificationsTooltip:Model]', error);
  }

  private handleReady(...args: unknown[]) {
    const [inst, state] = args as [inst: TReadyInstance, state: IState];
    // отписываемся, если не активно, но подписан
    const { timeMarkDelta, useLocalStorageTimemark, isEnabled } = this.params;
    if (!isEnabled && state.isSubscribed) {
      void inst.unsubscribe();
    } else if (isEnabled) {
      checkTimeMark(timeMarkDelta, useLocalStorageTimemark)
        .then(() => {
          const permission = getPermission();
          if (typeof this.on?.ready === 'function') {
            this.on?.ready(Object.assign({}, state, { permission }));
          }

          observePermission(timeMarkDelta, useLocalStorageTimemark);
        })
        .catch(this.handleError.bind(this));
    }
  }
}
